import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

interface SearchTabsProps {
  className?: string;
}

export const SearchTabs: React.FC<SearchTabsProps> = ({ className }) => {
  const [displayQuery, setDisplayQuery] = useState('');
  const [sequenceQuery, setSequenceQuery] = useState('');
  const navigate = useNavigate();

  const handleDisplaySearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (displayQuery.trim()) {
      navigate(`/displays?q=${encodeURIComponent(displayQuery.trim())}`);
    }
  };

  const handleSequenceSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (sequenceQuery.trim()) {
      navigate(`/sequences?q=${encodeURIComponent(sequenceQuery.trim())}`);
    }
  };

  return (
    <Tabs defaultValue="displays" className={cn("w-full max-w-lg", className)}>
      <TabsList className="grid w-full grid-cols-2 bg-white/10 backdrop-blur-xl border border-white/20">
        <TabsTrigger
          value="displays"
          className="data-[state=active]:bg-green-500/90 data-[state=active]:text-white flex items-center gap-2"
        >
          <MapPin size={16} />
          <span className="hidden sm:inline">Search</span> Displays
        </TabsTrigger>
        <TabsTrigger
          value="sequences"
          className="data-[state=active]:bg-blue-500/90 data-[state=active]:text-white flex items-center gap-2"
        >
          <Sparkles size={16} />
          <span className="hidden sm:inline">Search</span> Sequences
        </TabsTrigger>
      </TabsList>

      <TabsContent value="displays" className="mt-4">
        <form onSubmit={handleDisplaySearch} className="bg-white/10 backdrop-blur-xl border border-white/20 p-1 rounded-full flex items-center">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-400" size={18} />
            <Input
              placeholder="Search for holiday displays near you..."
              className="border-0 bg-transparent pl-10 text-white placeholder:text-white/60 focus-visible:ring-0 focus-visible:ring-offset-0"
              value={displayQuery}
              onChange={(e) => setDisplayQuery(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            className="rounded-full ml-1 px-4 bg-green-500 hover:bg-green-600"
          >
            <Search size={18} className="mr-2" /> Search
          </Button>
        </form>
      </TabsContent>

      <TabsContent value="sequences" className="mt-4">
        <form onSubmit={handleSequenceSearch} className="bg-white/10 backdrop-blur-xl border border-white/20 p-1 rounded-full flex items-center">
          <div className="flex-1 relative">
            <Sparkles className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" size={18} />
            <Input
              placeholder="Search for light sequences to download..."
              className="border-0 bg-transparent pl-10 text-white placeholder:text-white/60 focus-visible:ring-0 focus-visible:ring-offset-0"
              value={sequenceQuery}
              onChange={(e) => setSequenceQuery(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            className="rounded-full ml-1 px-4 bg-blue-500 hover:bg-blue-600"
          >
            <Search size={18} className="mr-2" /> Search
          </Button>
        </form>
      </TabsContent>
    </Tabs>
  );
};
